import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, SUPER_TOKEN_KEY } from '../../api/client';
import { superOrgBtn, superOrgField, superOrgInput } from './superOrgPanels';

type ProviderRow = { key: string; label: string; estimatedUsd: number };
type ServiceRow = { service: string; usd: number };
type DayRow = { date: string; usd: number };

type PlatformSpend = {
  totalUsd: number;
  awsIncluded: boolean;
  aws: { accountId: string; totalUsd: number; byService: ServiceRow[]; byDay: DayRow[]; error: string | null };
  llm: { totalUsd: number; byProvider: ProviderRow[]; modelsWithoutPrice: string[] };
  note: string;
};

const CHART_W = 860;
const CHART_H = 220;
const PAD_L = 46;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 32;

function dayLabel(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year!, month! - 1, day!)).toLocaleDateString('es-UY', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function axisUsd(amount: number): string {
  if (amount <= 0) return '0';
  return amount.toLocaleString('es-UY', { maximumFractionDigits: amount < 10 ? 2 : 0 });
}

function AwsDailyChart({ days }: { days: DayRow[] }) {
  const [selected, setSelected] = useState(Math.max(0, days.length - 1));
  const index = Math.min(selected, Math.max(0, days.length - 1));
  const active = days[index];
  const max = Math.max(0, ...days.map((day) => day.usd));
  const axisMax = max > 0 ? max : 1;
  const plotW = CHART_W - PAD_L - PAD_R;
  const plotH = CHART_H - PAD_T - PAD_B;
  const slot = days.length > 0 ? plotW / days.length : plotW;
  const labelStep = Math.max(1, Math.ceil(days.length / 6));
  const ticks = [axisMax, axisMax / 2, 0];

  return (
    <div>
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} role="img" aria-label="Gasto de AWS por día" style={{ width: '100%', height: 'auto', background: '#fff', borderRadius: 8 }}>
        {ticks.map((tick) => {
          const y = PAD_T + plotH - (tick / axisMax) * plotH;
          return (
            <g key={tick}>
              <line x1={PAD_L} x2={CHART_W - PAD_R} y1={y} y2={y} stroke="#e2e8f0" strokeWidth={1} />
              <text x={PAD_L - 8} y={y + 4} textAnchor="end" fill="#94a3b8" fontSize={11}>
                {axisUsd(tick)}
              </text>
            </g>
          );
        })}
        {days.map((day, dayIndex) => {
          const barH = day.usd > 0 ? Math.max(2, (day.usd / axisMax) * plotH) : 0;
          const x = PAD_L + slot * dayIndex;
          const on = dayIndex === index;
          return (
            <g key={day.date} onClick={() => setSelected(dayIndex)} style={{ cursor: 'pointer' }}>
              <rect x={x} y={PAD_T} width={slot} height={plotH} fill="transparent" />
              <rect
                x={x + slot * 0.18}
                y={PAD_T + plotH - barH}
                width={Math.max(1, slot * 0.64)}
                height={barH}
                rx={2}
                fill={on ? '#6d28d9' : '#7c3aed'}
              >
                <title>{`${dayLabel(day.date)}: ${usd(day.usd)}`}</title>
              </rect>
              {dayIndex % labelStep === 0 ? (
                <text x={x + slot / 2} y={CHART_H - 8} textAnchor="middle" fill="#64748b" fontSize={11}>
                  {dayLabel(day.date)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {active ? (
        <p style={{ margin: '8px 0 0', fontSize: 14 }}>
          {dayLabel(active.date)}: <strong>{usd(active.usd)}</strong>
        </p>
      ) : null}
    </div>
  );
}

function usd(amount: number): string {
  return `US$ ${amount.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function SuperSpendPage() {
  const token = localStorage.getItem(SUPER_TOKEN_KEY);
  const [from, setFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return isoDay(date);
  });
  const [to, setTo] = useState(() => isoDay(new Date()));
  const [data, setData] = useState<PlatformSpend | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const calculate = async (fromDay = from, toDay = to) => {
    if (!token) return;
    setLoading(true);
    setErr('');
    try {
      const fromIso = new Date(`${fromDay}T00:00:00.000Z`).toISOString();
      const toIso = new Date(`${toDay}T23:59:59.999Z`).toISOString();
      const result = await apiGet<PlatformSpend>(
        `/api/super/platform-spend?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
        token,
      );
      setData(result);
    } catch (error) {
      setData(null);
      setErr((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void calculate();
    // El botón Gasto total abre esta pantalla y el cálculo arranca solo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ flex: 1, minHeight: 0, background: '#f8fafc' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
        <p style={{ margin: '0 0 8px' }}>
          <Link to="/super/orgs" style={{ color: '#7c3aed', textDecoration: 'none' }}>
            Empresas
          </Link>
        </p>
        <h1 style={{ fontSize: 22, margin: '0 0 8px' }}>Gasto total</h1>
        <p style={{ color: '#64748b', margin: '0 0 16px' }}>
          Suma el AWS de la cuenta 263714505741, Claude y el resto de modelos del período.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
          <label style={superOrgField}>
            Desde
            <input style={superOrgInput} type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <label style={superOrgField}>
            Hasta
            <input style={superOrgInput} type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} />
          </label>
          <button type="button" style={superOrgBtn} disabled={loading} onClick={() => void calculate()}>
            {loading ? 'Calculando…' : 'Calcular'}
          </button>
        </div>
        {err ? <p style={{ color: '#b91c1c' }}>{err}</p> : null}
        {data ? (
          <>
            <p style={{ fontSize: 18, fontWeight: 700, margin: '8px 0' }}>
              Total: <span style={{ color: '#16a34a' }}>{usd(data.totalUsd)}</span>
              {data.awsIncluded ? null : <span style={{ fontWeight: 500, color: '#b45309' }}> (sin AWS)</span>}
            </p>
            <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{data.note}</p>

            <h2 style={{ fontSize: 16, margin: '20px 0 8px' }}>AWS por día</h2>
            <p style={{ margin: '0 0 8px', color: '#64748b', fontSize: 13 }}>Cuenta {data.aws.accountId}</p>
            {data.aws.error ? <p style={{ color: '#b45309' }}>{data.aws.error}</p> : null}
            {data.aws.byDay.length > 0 ? (
              <AwsDailyChart key={`${data.aws.byDay[0]?.date}-${data.aws.byDay.at(-1)?.date}`} days={data.aws.byDay} />
            ) : data.aws.error ? null : (
              <p style={{ color: '#94a3b8' }}>Sin cargos de AWS en el período.</p>
            )}

            <h2 style={{ fontSize: 16, margin: '20px 0 8px' }}>AWS por servicio</h2>
            {data.aws.byService.length > 0 ? (
              <>
                <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Subtotal AWS: {usd(data.aws.totalUsd)}</p>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: 14 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#64748b', background: '#f8fafc' }}>
                      <th style={{ padding: '10px 12px' }}>Servicio</th>
                      <th style={{ padding: '10px 12px' }}>USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.aws.byService.map((row) => (
                      <tr key={row.service} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '10px 12px' }}>{row.service}</td>
                        <td style={{ padding: '10px 12px' }}>{usd(row.usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : data.aws.error ? null : (
              <p style={{ color: '#94a3b8' }}>Sin cargos de AWS en el período.</p>
            )}

            <h2 style={{ fontSize: 16, margin: '20px 0 8px' }}>Modelos</h2>
            <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Subtotal modelos: {usd(data.llm.totalUsd)}</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#64748b', background: '#f8fafc' }}>
                  <th style={{ padding: '10px 12px' }}>Proveedor</th>
                  <th style={{ padding: '10px 12px' }}>USD estimado</th>
                </tr>
              </thead>
              <tbody>
                {data.llm.byProvider.map((row) => (
                  <tr key={row.key} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '10px 12px' }}>{row.label}</td>
                    <td style={{ padding: '10px 12px' }}>{usd(row.estimatedUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.llm.modelsWithoutPrice.length > 0 ? (
              <p style={{ color: '#b45309', fontSize: 13 }}>
                Sin tarifa: {data.llm.modelsWithoutPrice.join(', ')}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
